import React from "react";
import { Box, Typography } from "@mui/material";
import { LineChart } from "@outpave/ds";
import dayjs from "utils/dayjs";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "./CustomSkeleton";
import { formatCash, formatCashFull } from "utils/formatCash";

/** One point on the series. `x` is an ISO date string, `y` a decimal amount. */
export interface SpendAreaPoint {
  x: string;
  y: number;
}

export interface SpendAreaChartProps {
  data: SpendAreaPoint[];
  /** Currency for the tooltip and the axis. */
  currency?: string;
  loading?: boolean;
  /** Roughly how many x-axis labels to show. Defaults to 3. */
  tickCount?: number;
  /** Shown in place of the plot when every point is zero. */
  emptyMessage?: string;
  minHeight?: number;
}

/**
 * A money-over-time area chart, with no knowledge of what it is plotting.
 *
 * Presentational only: callers own the Panel, the heading and any headline
 * figure, the same way `PlatformBalanceChart` wraps `SpendGraph`. One series,
 * so it renders no legend.
 */
const SpendAreaChart: React.FC<SpendAreaChartProps> = ({
  data,
  currency = "usd",
  loading = false,
  tickCount = 3,
  emptyMessage,
  minHeight = 160,
}) => {
  const { t } = useTranslation();

  const hasData = data.some((point) => point.y > 0);

  // Floored at 1 so a tickCount of 0 cannot divide to Infinity.
  const labelStride = Math.max(
    1,
    Math.floor(data.length / Math.max(1, tickCount)),
  );

  if (loading) {
    return (
      <Box sx={{ width: "100%", flex: 1, minHeight }}>
        <CustomSkeleton variant="graph" />
      </Box>
    );
  }

  if (!hasData) {
    return (
      <Box sx={{ width: "100%", flex: 1, minHeight }}>
        <Typography
          variant="body1"
          sx={{
            color: "var(--color-text-tertiary)",
            textAlign: "center",
            padding: "var(--space-8) 0",
          }}
        >
          {emptyMessage ?? t("No spend in this period")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", flex: 1, minHeight }}>
      <Box sx={{ position: "absolute", inset: 0 }}>
        <LineChart
          fill
          legend={false}
          curve="monotone"
          series={[
            {
              id: "spend",
              area: true,
              showMark: false,
              data: data.map((point) => point.y),
              valueFormatter: (value) => formatCashFull(value, currency) ?? "",
            },
          ]}
          xAxis={[
            {
              data: data.map((point) => point.x),
              scaleType: "point",
              disableLine: true,
              disableTicks: true,
              valueFormatter: (date) => dayjs(String(date)).format("MMM D"),
              // The last point sits on the plot edge, where its label is cut off.
              tickInterval: (_date, index) =>
                index % labelStride === 0 && index < data.length - 1,
            },
          ]}
          yAxis={[
            {
              min: 0,
              disableLine: true,
              disableTicks: true,
              valueFormatter: (value) =>
                formatCash(Number(value), currency) ?? "",
            },
          ]}
          grid={{ horizontal: true }}
        />
      </Box>
    </Box>
  );
};

export default SpendAreaChart;
